import { cn } from "@/lib/utils";
import { Button } from "../atoms/button";

interface CounterInputProps {
  isCustomTemplate?: boolean;
  label: string;
  value: number;
  onChange?: (value: number) => void;
  onIncrement?: (value: number) => void;
  onDecrement?: (value: number) => void;
  labelClassName?: string;
  disabled?: boolean;
  minValue?: number;
  maxValue?: number;
  design?:string;
}
/**
 * CounterInput Component
 *
 * A reusable counter input component with increment and decrement buttons.
 * Useful for numeric inputs such as guest count, room count, etc.
 * Supports min/max limits, disabled state, and optional external handlers.
 */

const CounterInput: React.FC<CounterInputProps> = ({
  isCustomTemplate,
  label,
  value = 1,
  onChange,
  onIncrement,
  onDecrement,
  disabled,
  labelClassName,
  minValue = 0,
  maxValue,
  design
}) => {
  /**
   * Increments the counter value.
   * - Prevents exceeding maxValue if defined.
   * - Calls onIncrement or falls back to onChange if provided.
   */
  const handleIncrement = () => {
    if (maxValue !== undefined && value >= maxValue) return;
    if (onIncrement) {
      onIncrement(value + 1);
    } else if (onChange) {
      onChange(value + 1);
    }
  };
  /**
   * Decrements the counter value.
   * - Prevents going below minValue.
   * - Calls onDecrement or falls back to onChange if provided.
   */
  const handleDecrement = () => {
    if (value > minValue) {
      if (onDecrement) {
        onDecrement(value - 1);
      } else if (onChange) {
        onChange(value - 1);
      }
    }
  };

  const isCriDesign = design === "cri";


  return (
    <div className="flex min-w-full flex-col gap-1.5">
      <label
        className={cn(
          `w-full text-left text-sm leading-5 font-medium ${isCriDesign ? "text-[#223B1D]" : "text-[var(--text-gray-normal)]"}`,
          labelClassName,
        )}
      >
        {label}
      </label>
      <div className="flex items-center">
        <Button
          className={cn(isCustomTemplate ? "rounded-md" : "rounded-full")}
          onClick={handleDecrement}
          type="button"
          size={"lg"}
          hierarchy={isCustomTemplate ? "outline" : "tonal"}
          disabled={disabled || value <= minValue}
        >
          -
        </Button>
        <span className={`min-w-9 text-center leading-6 ${isCriDesign ? "text-[#223B1D]" : "text-[var(--text-base-primary)]"}`}>
          {value}
        </span>
        <Button
          className={cn(isCustomTemplate ? isCriDesign?"bg-[#223B1D] text-white" : "rounded-md" : "rounded-full")}
          size={"lg"}
          onClick={handleIncrement}
          type="button"
          disabled={disabled || (maxValue !== undefined && value >= maxValue)}
          hierarchy={isCustomTemplate ? "filled" : "tonal"}
        >
          +
        </Button>
      </div>
    </div>
  );
};

export default CounterInput;
